/**
 * VendorManagementPage — Admin Vendor Dashboard
 *
 * Calls:  GET /marketplace/vendors
 * Displays: Name, Type, Status, Bank verified, Action buttons
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Switch,
  FormControlLabel,
  Rating,
} from '@mui/material';
import {
  Storefront,
  Search,
  FilterList,
  Refresh,
  Visibility,
  Close,
  CheckCircle,
  Cancel,
  VerifiedUser,
  AccountBalanceWallet,
  Star,
  Phone,
  Email,
  LocationOn,
  Business,
} from '@mui/icons-material';
import { marketplaceAPI } from 'services/api';

// ─── Helpers ────────────────────────────────────────────────
const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  const n = parseFloat(amount);
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const formatDate = (dt) => {
  if (!dt) return '—';
  return new Date(dt).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// ─── Status badge ────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const s = (status || '').toLowerCase();
  const cfg = {
    active:               { label: 'Active',              color: 'success' },
    inactive:             { label: 'Inactive',            color: 'default' },
    suspended:            { label: 'Suspended',           color: 'error' },
    pending_verification: { label: 'Pending Verification', color: 'warning' },
  };
  const c = cfg[s] || { label: status || 'Unknown', color: 'default' };
  return (
    <Chip
      label={c.label}
      color={c.color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 24 }}
    />
  );
};

// ═════════════════════════════════════════════════════════════
const VendorManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [vendors, setVendors] = useState([]);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // Detail dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const STATUSES = ['ALL', 'active', 'inactive', 'suspended', 'pending_verification'];

  // ── Load Data ─────────────────────────────────────────────
  const loadData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setError(null);

      const res = await marketplaceAPI.getVendors();
      setVendors(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('VendorManagement load error:', err);
      setError('Failed to load vendor data.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── View Detail ────────────────────────────────────────────
  const viewVendor = async (vendor) => {
    setDialogOpen(true);
    setSelectedVendor(vendor);
    try {
      setDetailLoading(true);
      const res = await marketplaceAPI.getVendor(vendor.id);
      setSelectedVendor(res.data);
    } catch {
      // keep what we have
    } finally {
      setDetailLoading(false);
    }
  };

  // ── Actions ────────────────────────────────────────────────
  const toggleVerification = async (vendor) => {
    try {
      setActionLoading(true);
      await marketplaceAPI.verifyVendor(vendor.id, !vendor.verified);
      await loadData(true);
      if (selectedVendor?.id === vendor.id) {
        const res = await marketplaceAPI.getVendor(vendor.id);
        setSelectedVendor(res.data);
      }
    } catch (err) {
      console.error('Verify error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const updateStatus = async (vendor, newStatus) => {
    try {
      setActionLoading(true);
      await marketplaceAPI.updateVendorStatus(vendor.id, newStatus);
      await loadData(true);
      if (selectedVendor?.id === vendor.id) {
        const res = await marketplaceAPI.getVendor(vendor.id);
        setSelectedVendor(res.data);
      }
    } catch (err) {
      console.error('Status update error:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────
  const filtered = useMemo(() => {
    return vendors.filter((v) => {
      const matchSearch =
        !search ||
        (v.business_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (v.contact_person || '').toLowerCase().includes(search.toLowerCase()) ||
        (v.phone || '').includes(search) ||
        (v.email || '').toLowerCase().includes(search.toLowerCase()) ||
        (v.vendor_code || '').toLowerCase().includes(search.toLowerCase());
      const matchStatus =
        statusFilter === 'ALL' || (v.status || '').toLowerCase() === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [vendors, search, statusFilter]);

  const activeCount = vendors.filter((v) => (v.status || '').toLowerCase() === 'active').length;
  const verifiedCount = vendors.filter((v) => v.verified).length;
  const pendingCount = vendors.filter((v) => (v.status || '').toLowerCase() === 'pending_verification').length;
  const totalSales = vendors.reduce((s, v) => s + (parseFloat(v.total_sales) || 0), 0);

  // ── Loading ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <Storefront sx={{ fontSize: 22, color: '#ea580c' }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Management</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <CircularProgress size={32} sx={{ color: '#ea580c' }} />
          <span className="ml-3 text-sm text-gray-500">Loading vendor data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
              <Storefront sx={{ fontSize: 22, color: '#ea580c' }} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Vendor Management</h1>
          </div>
          <p className="text-sm text-gray-500 ml-[52px]">
            Manage vendors, onboarding status, ratings, and compliance
          </p>
        </div>
        <Tooltip title="Refresh data">
          <IconButton
            onClick={() => loadData(true)}
            disabled={refreshing}
            sx={{ backgroundColor: '#f1f5f9', '&:hover': { backgroundColor: '#e2e8f0' } }}
          >
            <Refresh
              sx={{
                fontSize: 20,
                animation: refreshing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
              }}
            />
          </IconButton>
        </Tooltip>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} action={<Button size="small" onClick={() => loadData(true)}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {refreshing && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-2xl bg-orange-50 border border-orange-100 p-5">
          <p className="text-xs text-orange-600 font-medium mb-1">Total Vendors</p>
          <p className="text-2xl font-bold text-orange-800">{vendors.length}</p>
          <p className="text-xs text-orange-500 mt-1">{activeCount} active</p>
        </div>
        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-5">
          <p className="text-xs text-emerald-600 font-medium mb-1">Verified</p>
          <p className="text-2xl font-bold text-emerald-800">{verifiedCount}</p>
          <p className="text-xs text-emerald-500 mt-1">
            {vendors.length > 0 ? `${((verifiedCount / vendors.length) * 100).toFixed(0)}%` : '0%'} of total
          </p>
        </div>
        <div className="rounded-2xl bg-amber-50 border border-amber-100 p-5">
          <p className="text-xs text-amber-600 font-medium mb-1">Pending Verification</p>
          <p className="text-2xl font-bold text-amber-800">{pendingCount}</p>
          <p className="text-xs text-amber-500 mt-1">Awaiting review</p>
        </div>
        <div className="rounded-2xl bg-indigo-50 border border-indigo-100 p-5">
          <p className="text-xs text-indigo-600 font-medium mb-1">Total Sales</p>
          <p className="text-2xl font-bold text-indigo-800">{formatCurrency(totalSales)}</p>
          <p className="text-xs text-indigo-500 mt-1">Across all vendors</p>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <TextField
              placeholder="Search by name, phone, email, or code..."
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: '#9ca3af' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 280 }}
            />

            <div className="flex items-center gap-1 flex-wrap">
              <FilterList sx={{ fontSize: 16, color: '#9ca3af', mr: 0.5 }} />
              {STATUSES.map((s) => (
                <Chip
                  key={s}
                  label={s === 'ALL' ? 'All' : s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                  variant={statusFilter === s ? 'filled' : 'outlined'}
                  color={statusFilter === s ? 'primary' : 'default'}
                  size="small"
                  onClick={() => setStatusFilter(s)}
                  sx={{ fontWeight: 500, fontSize: '0.65rem', cursor: 'pointer' }}
                />
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Showing {filtered.length} of {vendors.length} vendors
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vendor</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Bank Verified</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rating</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sales</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-400 text-sm">
                    {vendors.length === 0
                      ? 'No vendors registered yet.'
                      : 'No vendors match your filters'}
                  </td>
                </tr>
              ) : (
                filtered.map((v) => (
                  <tr key={v.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-800">{v.business_name}</p>
                        <p className="text-xs text-gray-400 font-mono">{v.vendor_code}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Chip
                        label={(v.business_type || 'general').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 500, fontSize: '0.6rem', height: 22 }}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-700">{v.contact_person}</p>
                      <p className="text-xs text-gray-400">{v.phone}</p>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={v.status} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {v.verified ? (
                        <Tooltip title="Bank Verified">
                          <VerifiedUser sx={{ fontSize: 18, color: '#10b981' }} />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Not Verified">
                          <Cancel sx={{ fontSize: 18, color: '#d1d5db' }} />
                        </Tooltip>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {v.average_rating > 0 ? (
                        <div className="flex items-center gap-1">
                          <Star sx={{ fontSize: 14, color: '#f59e0b' }} />
                          <span className="text-sm font-medium text-gray-700">
                            {parseFloat(v.average_rating).toFixed(1)}
                          </span>
                          <span className="text-xs text-gray-400">({v.total_ratings})</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No ratings</span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-700">
                      {formatCurrency(v.total_sales)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => viewVendor(v)}>
                            <Visibility sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        {!v.verified && (
                          <Tooltip title="Verify Vendor">
                            <IconButton
                              size="small"
                              onClick={() => toggleVerification(v)}
                              disabled={actionLoading}
                            >
                              <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(v.status || '').toLowerCase() === 'active' && (
                          <Tooltip title="Suspend Vendor">
                            <IconButton
                              size="small"
                              onClick={() => updateStatus(v, 'suspended')}
                              disabled={actionLoading}
                            >
                              <Cancel sx={{ fontSize: 16, color: '#ef4444' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(v.status || '').toLowerCase() === 'suspended' && (
                          <Tooltip title="Reactivate Vendor">
                            <IconButton
                              size="small"
                              onClick={() => updateStatus(v, 'active')}
                              disabled={actionLoading}
                            >
                              <CheckCircle sx={{ fontSize: 16, color: '#0ea5e9' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detail Dialog ── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <span className="text-lg font-bold">Vendor Details</span>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <CircularProgress size={28} />
            </div>
          ) : selectedVendor ? (
            <div className="space-y-5">
              {/* Name + Code */}
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedVendor.business_name}</h3>
                  {selectedVendor.legal_name && (
                    <p className="text-xs text-gray-400">{selectedVendor.legal_name}</p>
                  )}
                  <p className="text-xs text-gray-400 font-mono mt-1">{selectedVendor.vendor_code}</p>
                </div>
                <StatusBadge status={selectedVendor.status} />
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-3">
                <InfoRow icon={<Business />} label="Type" value={(selectedVendor.business_type || 'General').replace(/_/g, ' ')} />
                <InfoRow icon={<Phone />} label="Phone" value={selectedVendor.phone} />
                <InfoRow icon={<Email />} label="Email" value={selectedVendor.email} />
                <InfoRow icon={<LocationOn />} label="City" value={selectedVendor.city || '—'} />
              </div>

              {/* Contact person */}
              <div className="p-3 rounded-xl bg-gray-50">
                <p className="text-xs text-gray-400 mb-1">Contact Person</p>
                <p className="text-sm font-medium text-gray-800">{selectedVendor.contact_person}</p>
                <p className="text-xs text-gray-500">{selectedVendor.address}</p>
              </div>

              {/* Compliance */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-400 mb-1">GST Number</p>
                  <p className="text-sm font-mono text-gray-700">{selectedVendor.gst_number || '—'}</p>
                </div>
                <div className="p-3 rounded-xl bg-gray-50">
                  <p className="text-xs text-gray-400 mb-1">PAN Number</p>
                  <p className="text-sm font-mono text-gray-700">{selectedVendor.pan_number || '—'}</p>
                </div>
              </div>

              {/* Bank */}
              <div className="p-3 rounded-xl bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400">Bank Details</p>
                  {selectedVendor.verified ? (
                    <Chip icon={<VerifiedUser sx={{ fontSize: 12 }} />} label="Verified" color="success" size="small" sx={{ height: 22, fontSize: '0.6rem' }} />
                  ) : (
                    <Chip label="Not Verified" color="default" size="small" variant="outlined" sx={{ height: 22, fontSize: '0.6rem' }} />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-xs text-gray-400">Account</span>
                    <p className="font-mono text-gray-700">{selectedVendor.bank_account || '—'}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-400">IFSC</span>
                    <p className="font-mono text-gray-700">{selectedVendor.bank_ifsc || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs text-gray-400">Bank</span>
                    <p className="text-gray-700">{selectedVendor.bank_name || '—'}</p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-xl bg-indigo-50 text-center">
                  <p className="text-2xl font-bold text-indigo-800">{selectedVendor.total_orders || 0}</p>
                  <p className="text-xs text-indigo-500">Orders</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50 text-center">
                  <p className="text-2xl font-bold text-emerald-800">{formatCurrency(selectedVendor.total_sales)}</p>
                  <p className="text-xs text-emerald-500">Sales</p>
                </div>
                <div className="p-3 rounded-xl bg-amber-50 text-center">
                  {selectedVendor.average_rating > 0 ? (
                    <>
                      <div className="flex items-center justify-center gap-1">
                        <p className="text-2xl font-bold text-amber-800">
                          {parseFloat(selectedVendor.average_rating).toFixed(1)}
                        </p>
                        <Star sx={{ fontSize: 18, color: '#f59e0b' }} />
                      </div>
                      <p className="text-xs text-amber-500">{selectedVendor.total_ratings} ratings</p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-amber-800">—</p>
                      <p className="text-xs text-amber-500">No ratings</p>
                    </>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!selectedVendor.verified}
                      onChange={() => toggleVerification(selectedVendor)}
                      disabled={actionLoading}
                      size="small"
                      color="success"
                    />
                  }
                  label={<span className="text-sm">{selectedVendor.verified ? 'Verified' : 'Verify Vendor'}</span>}
                />
                <div className="ml-auto flex gap-2">
                  {(selectedVendor.status || '').toLowerCase() === 'active' && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() => updateStatus(selectedVendor, 'suspended')}
                      disabled={actionLoading}
                      sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem' }}
                    >
                      Suspend
                    </Button>
                  )}
                  {['suspended', 'inactive'].includes((selectedVendor.status || '').toLowerCase()) && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => updateStatus(selectedVendor, 'active')}
                      disabled={actionLoading}
                      sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem' }}
                    >
                      Activate
                    </Button>
                  )}
                </div>
              </div>

              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t border-gray-100">
                <div>
                  <span className="text-gray-400">Created</span>
                  <p className="text-gray-600">{formatDate(selectedVendor.created_at)}</p>
                </div>
                {selectedVendor.verified_at && (
                  <div>
                    <span className="text-gray-400">Verified On</span>
                    <p className="text-gray-600">{formatDate(selectedVendor.verified_at)}</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} variant="outlined" size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ─── Info Row helper ─────────────────────────────────────────
const InfoRow = ({ icon, label, value }) => (
  <div className="flex items-start gap-2">
    {React.cloneElement(icon, { sx: { fontSize: 16, color: '#9ca3af', mt: '2px' } })}
    <div>
      <p className="text-xs text-gray-400">{label}</p>
      <p className="text-sm text-gray-700">{value || '—'}</p>
    </div>
  </div>
);

export default VendorManagementPage;
